import { api } from './client'

/** One step of a drip sequence. Channel is email. */
export interface DripStep {
  /** Legacy whole-day delay; still read for sequences created before sub-day cadences. */
  dayOffset?: number
  /** Delay after enrolment in minutes — the canonical field (minutes / hours / days in the UI). */
  delayMinutes?: number
  templateId?: string
  subject?: string
  body?: string
}

/** Delay units offered in the step editor, as multiples of a minute. */
export const DELAY_UNITS = [
  { value: 'minutes', label: 'minutes', perUnit: 1 },
  { value: 'hours', label: 'hours', perUnit: 60 },
  { value: 'days', label: 'days', perUnit: 60 * 24 },
] as const

/** A step's delay in minutes, tolerating the legacy day-based shape. */
export function stepDelayMinutes(step: DripStep): number {
  if (step.delayMinutes != null) return Math.max(0, step.delayMinutes)
  return Math.max(0, step.dayOffset ?? 0) * 60 * 24
}

/** Split a minute count into the largest whole unit that represents it exactly. */
export function splitDelay(minutes: number): { value: number; unit: (typeof DELAY_UNITS)[number]['value'] } {
  for (const u of [...DELAY_UNITS].reverse()) {
    if (minutes >= u.perUnit && minutes % u.perUnit === 0) {
      return { value: minutes / u.perUnit, unit: u.value }
    }
  }
  return { value: minutes, unit: 'minutes' }
}

export interface DripSequence {
  id: string
  name: string
  enabled: boolean
  steps: DripStep[]
  activeEnrollments: number
  /** Contacts who received every step. */
  completedEnrollments?: number
  /** Local hours (HH:mm[:ss]) this sequence may send in; null = send as soon as a step matures. */
  sendWindowStart?: string | null
  sendWindowEnd?: string | null
  createdAt: string
}

export interface DripSequenceInput {
  name: string
  enabled?: boolean
  steps: DripStep[]
  sendWindowStart?: string | null
  sendWindowEnd?: string | null
}

export interface EnrollInput {
  contactIds?: string[]
  listId?: string
}

export async function listDripSequences(): Promise<DripSequence[]> {
  const { data } = await api.get<DripSequence[]>('/api/drip-sequences')
  return data
}

export async function createDripSequence(input: DripSequenceInput): Promise<DripSequence> {
  const { data } = await api.post<DripSequence>('/api/drip-sequences', input)
  return data
}

export async function updateDripSequence(id: string, input: DripSequenceInput): Promise<DripSequence> {
  const { data } = await api.put<DripSequence>(`/api/drip-sequences/${id}`, input)
  return data
}

export async function deleteDripSequence(id: string): Promise<void> {
  await api.delete(`/api/drip-sequences/${id}`)
}

export async function enrollInDrip(id: string, input: EnrollInput): Promise<{ enrolled: number }> {
  const { data } = await api.post<{ enrolled: number }>(`/api/drip-sequences/${id}/enroll`, input)
  return data
}

/** One contact's place in a sequence. */
export interface Enrollment {
  id: string
  contactId: string
  contactName: string
  email: string | null
  status: 'active' | 'completed' | 'cancelled'
  /** Emails already sent to them. */
  stepsSent: number
  nextRunAt: string
  enrolledAt: string
}

export async function listEnrollments(sequenceId: string): Promise<Enrollment[]> {
  return (await api.get<Enrollment[]>(`/api/drip-sequences/${sequenceId}/enrollments`)).data
}

export async function cancelEnrollment(sequenceId: string, enrollmentId: string): Promise<void> {
  await api.post(`/api/drip-sequences/${sequenceId}/enrollments/${enrollmentId}/cancel`)
}
